import styled from "@emotion/styled";
import {
  StatusCircleEmpty,
  StatusCircleQuarter,
  StatusCircleHalfIcon,
  StatusCircleThreeQuarterIcon,
  StatusCircleSevenEighthsIcon,
  StatusCircleCompleted,
  CancelCircle,
  UploadCircleOutlinedIcon,
  ChevronUp,
  ChevronPaginationDownIcon,
} from "src/assets/icons";
import { Clock01Icon } from "@porchpass/ui";
import {
  PRIMARY_BLACK,
  DRAFT_BG,
  DRAFT_TEXT,
  AWAITING_SIGNATURE_BG,
  AWAITING_SIGNATURE_TEXT,
  INVITED_BG,
  INVITED_TEXT,
  REOPENED_FOR_EDIT_BG,
  REOPENED_FOR_EDIT_TEXT,
  SUBMITTED_BG,
  SUBMITTED_TEXT,
  SUBMISSION_FAILED_BG,
  SUBMISSION_FAILED_TEXT,
  PRE_APPROVED_BG,
  PRE_APPROVED_TEXT,
  CLEAR_TO_CLOSE_BG,
  CLEAR_TO_CLOSE_TEXT,
  CLOSED_BG,
  CLOSED_TEXT,
  FUNDED_BG,
  FUNDED_TEXT,
  DECLINED_BG,
  DECLINED_TEXT,
  READY_TO_SUBMIT_BG,
  READY_TO_SUBMIT_TEXT,
} from "src/constants/colors";
import { APPLICATION_STATUSES } from "src/constants";

const BaseWrapper = styled.div<{
  backgroundColor: string;
  textColor: string;
  isDropdown?: boolean;
}>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 5px;
  color: ${({ textColor }) => textColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  gap: 6px;
  max-height: 20px;
  padding: 4px 6px;
  white-space: nowrap;
  width: fit-content;
  cursor: ${({ isDropdown }) => (isDropdown ? "pointer" : "")};
`;

export type AppMilestoneType =
  | "Draft"
  | "Awaiting Signature"
  | "Invited"
  | "Re-opened for edit"
  | "Submitted"
  | "Submission failed"
  | "Pre-approved"
  | "Clear to close"
  | "Closed"
  | "Funded"
  | "Declined"
  | "Ready to submit";

interface ApplicationStatusBadgeProps {
  status: AppMilestoneType;
  isDropdown?: boolean;
  isOpen?: boolean;
}

export const ApplicationStatusBadge: React.FC<ApplicationStatusBadgeProps> = ({
  status,
  isDropdown = false,
  isOpen = false,
}) => {
  const renderChevron = () => {
    if (!isDropdown) return null;
    return isOpen ? (
      <ChevronUp fill={PRIMARY_BLACK} />
    ) : (
      <ChevronPaginationDownIcon fill={PRIMARY_BLACK} />
    );
  };

  let WrappedStatus = <div>{status}</div>;
  if (status === APPLICATION_STATUSES.DRAFT) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={DRAFT_BG}
        textColor={DRAFT_TEXT}
        isDropdown={isDropdown}
      >
        <StatusCircleEmpty />
        {APPLICATION_STATUSES.DRAFT}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.AWAITING_SIGNATURE) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={AWAITING_SIGNATURE_BG}
        textColor={AWAITING_SIGNATURE_TEXT}
        isDropdown={isDropdown}
      >
        <Clock01Icon size={12} />
        {APPLICATION_STATUSES.AWAITING_SIGNATURE}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.INVITED) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={INVITED_BG}
        textColor={INVITED_TEXT}
        isDropdown={isDropdown}
      >
        <StatusCircleEmpty />
        {APPLICATION_STATUSES.INVITED}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.REOPENED_FOR_EDIT) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={REOPENED_FOR_EDIT_BG}
        textColor={REOPENED_FOR_EDIT_TEXT}
        isDropdown={isDropdown}
      >
        <StatusCircleEmpty />
        {APPLICATION_STATUSES.REOPENED_FOR_EDIT}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.SUBMITTED) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={SUBMITTED_BG}
        textColor={SUBMITTED_TEXT}
        isDropdown={isDropdown}
      >
        <StatusCircleQuarter />
        {APPLICATION_STATUSES.SUBMITTED}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.PRE_APPROVED) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={PRE_APPROVED_BG}
        textColor={PRE_APPROVED_TEXT}
        isDropdown={isDropdown}
      >
        <StatusCircleHalfIcon />
        {APPLICATION_STATUSES.PRE_APPROVED}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.CLEAR_TO_CLOSE) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={CLEAR_TO_CLOSE_BG}
        textColor={CLEAR_TO_CLOSE_TEXT}
        isDropdown={isDropdown}
      >
        <StatusCircleThreeQuarterIcon />
        {APPLICATION_STATUSES.CLEAR_TO_CLOSE}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.CLOSED) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={CLOSED_BG}
        textColor={CLOSED_TEXT}
        isDropdown={isDropdown}
      >
        <StatusCircleSevenEighthsIcon />
        {APPLICATION_STATUSES.CLOSED}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.FUNDED) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={FUNDED_BG}
        textColor={FUNDED_TEXT}
        isDropdown={isDropdown}
      >
        <StatusCircleCompleted />
        {APPLICATION_STATUSES.FUNDED}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.DECLINED) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={DECLINED_BG}
        textColor={DECLINED_TEXT}
        isDropdown={isDropdown}
      >
        <CancelCircle
          fillColor={DECLINED_TEXT}
          opacity="100"
          width={14}
          height={14}
        />
        {APPLICATION_STATUSES.DECLINED}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.READY_TO_SUBMIT) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={READY_TO_SUBMIT_BG}
        textColor={READY_TO_SUBMIT_TEXT}
        isDropdown={isDropdown}
      >
        <UploadCircleOutlinedIcon width={14} height={14} />
        {APPLICATION_STATUSES.READY_TO_SUBMIT}
        {renderChevron()}
      </BaseWrapper>
    );
  } else if (status === APPLICATION_STATUSES.SUBMISSION_FAILED) {
    WrappedStatus = (
      <BaseWrapper
        backgroundColor={SUBMISSION_FAILED_BG}
        textColor={SUBMISSION_FAILED_TEXT}
        isDropdown={isDropdown}
      >
        <CancelCircle
          fillColor={SUBMISSION_FAILED_TEXT}
          opacity="100"
          width={14}
          height={14}
        />
        {APPLICATION_STATUSES.SUBMISSION_FAILED}
        {renderChevron()}
      </BaseWrapper>
    );
  }

  return WrappedStatus;
};
